import React, { useState, useEffect } from 'react'
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useRecoilState } from 'recoil';
import { AddToCalendarButton } from 'add-to-calendar-button-react';
import dayjs from 'dayjs';
import {
  Button,
  Modal,
  Table,
  Divider,
  Select,
  Popconfirm,
  DatePicker
} from 'antd';
import {
  viewAppointmentsAtom,
  notificationsAtom,
  reloadLeadsAtom,
  viewLeadAtom
} from '../../../../atoms/Atoms';
import api from '../../../../api/api';
import { toFormattedLocalTime, mapAppointmentStatus, getMobileDatePickerFooter, toLocalTime } from '../../../../functions';
import { appointmentStatuses, appointmentTypes } from '../../../../utils';

function LeadAppointmentsModal() {
  const { t, i18n } = useTranslation();
  const { Option } = Select;
  const [open, setOpen] = useState(false);
  const [lead, setLead] = useRecoilState(viewAppointmentsAtom);
  const [loading, setLoading] = useState(false);
  const [reloadLeads, setReloadLeads] = useRecoilState(reloadLeadsAtom);
  const [selectedDate, setSelectedDate] = useState(dayjs().format('MMM DD, YYYY hh:mm a'));
  const [selectedStatus, setSelectedStatus] = useState(0);
  const [selectedType, setSelectedType] = useState(1);
  const [notifications, setNotifications] = useRecoilState(notificationsAtom);
  const [viewLead, setViewLead] = useRecoilState(viewLeadAtom);

  function getLead(id) {
    api.get(`leads/lead/${id}`)
    .then((response) => {
      if (response.data.success) {
        setViewLead(response.data.data);
      } else {
        setNotifications([
          ...notifications,
          {
            title: t('error'),
            description: response?.data?.error,
            error: true,
          },
        ]);
      }
    })
    .catch((error) => {
      setNotifications([
        ...notifications,
        {
          title: t('error'),
          description: error?.message,
          error: true,
        },
      ]);
    });
  }

  function create() {
    setLoading(true);
    const dateString = dayjs(selectedDate).add(4, 'hour').format('YYYY-MM-DDTHH:mm:ss');
    api.post('leads/appointments', {
        LeadId: lead?.id,
        AppointmentDate: dateString,
        AppointmentStatus: selectedStatus,
        Type: selectedType
      })
        .then((response) => {
            setLoading(false);
          if (response?.data?.error) {
            setNotifications([
              ...notifications,
              {
                title: t('error'),
                description: response?.data?.error,
                error: true,
              },
            ]);
          } else {
            setNotifications([
              ...notifications,
              {
                title: t('success'),
                description: response.data.message,
                error: false,
              },
            ]);
            setReloadLeads(true);
            setLead({
              ...lead,
              appointments: response.data.data
            });
          }
        })
        .catch((error) => {
          setLoading(false);
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: error.message,
              error: true,
            },
          ]);
        });
  }

  function update(appointment) {
    setLoading(true);
    const dateString = dayjs(appointment.appointmentDate).format('YYYY-MM-DDTHH:mm:ss');
    api.put('leads/appointments', {
        Id: appointment.id,
        AppointmentDate: dateString,
        AppointmentStatus: appointment.appointmentStatus,
        Type: appointment.type
      })
        .then((response) => {
          setLoading(false);
          if (response?.data?.error) {
            setNotifications([
              ...notifications,
              {
                title: t('error'),
                description: response?.data?.error,
                error: true,
              },
            ]);
          } else {
            setNotifications([
              ...notifications,
              {
                title: t('success'),
                description: response.data.message,
                error: false,
              },
            ]);
            setReloadLeads(true);
            if (viewLead) {
              getLead(appointment.leadId);
            }
            // contactedOn: response.data.data.contactedOn ? dayjs(response.data.data.contactedOn).add(4, 'hour') : null,
            setLead({
              ...lead,
              appointments: response.data.data
            });
          }
        })
        .catch((error) => {
          setLoading(false);
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: error.message,
              error: true,
            },
          ]);
        });
  }

  function remove(id) {
    setLoading(true);
    api.delete(`leads/appointments/${id}`)
        .then((response) => {
            setLoading(false);
          if (response?.data?.error) {
            setNotifications([
              ...notifications,
              {
                title: t('error'),
                description: response?.data?.error,
                error: true,
              },
            ]);
          } else {
            setNotifications([
              ...notifications,
              {
                title: t('success'),
                description: response.data.message,
                error: false,
              },
            ]);
            setReloadLeads(true);
            setLead({
              ...lead,
              appointments: response.data.data
            });
          }
        })
        .catch((error) => {
          setLoading(false);
          setNotifications([
            ...notifications,
            {
              title: t('error'),
              description: error.message,
              error: true,
            },
          ]);
        });
  }

  useEffect(() => {
    setSelectedDate(dayjs().format('MMM DD, YYYY hh:mm a'));
    setSelectedStatus(0);
    setSelectedType(1);
    if (lead) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [lead]);

  return (
    <Modal
      width="80%"
      title={`${t('appointments')} ${lead?.appointments?.length > 0 ? `(${lead?.appointments?.length})` : ''}`}
      open={open}
      onCancel={() => {
        if (viewLead) {
          setViewLead({
            ...viewLead,
            appointments: lead.appointments
          });
        }
        setLead(null);
      }}
      footer={[]}
    >
      <div className="flex flex-col">
        <p className="font-bold w-2/3 md:w-1/3 mx-auto">{t('appointment_date')}</p>
        <DatePicker
          showTime={{ use12Hours: true }}
          format="YYYY-MM-DD hh:mm a"
          className="w-2/3 md:w-1/3 mx-auto"
          showHour
          inputReadOnly
          showMinute
          minuteStep={15}
          value={dayjs(selectedDate)}
          onChange={(date) => {
            setSelectedDate(date.format('MMM DD, YYYY hh:mm a'));
          }}
          showSecond={false}
          disabled={loading}
          type="primary"
          size="small"
          renderExtraFooter={() => getMobileDatePickerFooter(t)}
          placement="bottomLeft"
        />

        <p className="font-bold w-2/3 md:w-1/3 mt-5 mx-auto">{t('status')}</p>
        <Select
          className="w-2/3 md:w-1/3 mx-auto"
          value={selectedStatus}
          disabled={loading}
          onChange={(obj) => setSelectedStatus(obj)}
        >
          {_.map(appointmentStatuses, (g) => (
            <Option value={g.id}>{t(g.name)}</Option>
          ))}
        </Select>

        <p className="font-bold w-2/3 md:w-1/3 mt-5 mx-auto">{t('type')}</p>
        <Select
          className="w-2/3 md:w-1/3 mx-auto"
          value={selectedType}
          disabled={loading}
          onChange={(obj) => setSelectedType(obj)}
        >
          {_.map(appointmentTypes, (g) => (
            <Option value={g.id}>{t(g.name)}</Option>
          ))}
        </Select>

        <Button
          loading={loading}
          type="primary"
          className="w-2/3 md:w-1/3 mt-2 mx-auto"
          onClick={() => create()}
        >
          {t('create_appointment')}
        </Button>
      </div>
      <Divider />
      <Table
        locale={{
          emptyText: t('no_data')
        }}
        bordered
        rowKey="id"
        loading={loading}
        dataSource={lead?.appointments}
        columns={[
          {
            title: '',
            align: 'center',
            className: 'text-sm md:hidden',
            render: (row) => (
              <div>
                <p className="font-bold">
                  {t('appointment_date')}:
                </p>
                <DatePicker
                  showTime={{ use12Hours: true }}
                  format="YYYY-MM-DD hh:mm a"
                  className="w-full"
                  showHour
                  inputReadOnly
                  showMinute
                  minuteStep={15}
                  value={dayjs(row?.appointmentDate).subtract(4, 'hour')}
                  onChange={(date) => {
                    const tempData = JSON.parse(JSON.stringify(lead));
                    for (let i = 0; i < tempData.appointments?.length; i++) {
                      if (tempData.appointments[i].id === row.id) {
                        tempData.appointments[i].appointmentDate = date.add(4, 'hour');
                      }
                    }
                    setLead(tempData);
                  }}
                  showSecond={false}
                  disabled={loading}
                  type="primary"
                  size="small"
                  renderExtraFooter={() => getMobileDatePickerFooter(t)}
                  placement="bottomLeft"
                />
                <p className="font-bold mt-5">
                  {t('status')}:
                </p>
                <Select
                  className="w-full"
                  value={row.appointmentStatus}
                  disabled={loading}
                  onChange={(obj) => {
                    const tempData = JSON.parse(JSON.stringify(lead));
                    for (let i = 0; i < tempData.appointments?.length; i++) {
                      if (tempData.appointments[i].id === row.id) {
                        tempData.appointments[i].appointmentStatus = obj;
                      }
                    }
                    setLead(tempData);
                  }}
                >
                  {_.map(appointmentStatuses, (g) => (
                    <Option value={g.id}>{t(g.name)}</Option>
                  ))}
                </Select>
                <p className="font-bold mt-5">
                  {t('type')}:
                </p>
                <Select
                  className="w-full"
                  value={row.type}
                  disabled={loading}
                  onChange={(obj) => {
                    const tempData = JSON.parse(JSON.stringify(lead));
                    for (let i = 0; i < tempData.appointments?.length; i++) {
                      if (tempData.appointments[i].id === row.id) {
                        tempData.appointments[i].type = obj;
                      }
                    }
                    setLead(tempData);
                  }}
                >
                  {_.map(appointmentTypes, (g) => (
                    <Option value={g.id}>{t(g.name)}</Option>
                  ))}
                </Select>
                <div className="grid grid-cols-2 mt-5 gap-2">
                  <Popconfirm
                    title={t('update_appointment_prompt')}
                    okText={t('yes')}
                    cancelText={t('no')}
                    onConfirm={() => update(row)}
                  >
                    <Button
                      type="primary"
                      size="small"
                      className="w-full"
                      disabled={loading}
                    >
                      {t('update')}
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title={t('delete_appointment_prompt')}
                    okText={t('yes')}
                    cancelText={t('no')}
                    onConfirm={() => remove(row.id)}
                  >
                    <Button
                      type="primary"
                      danger
                      size="small"
                      className="w-full"
                      disabled={loading}
                    >
                      {t('delete')}
                    </Button>
                  </Popconfirm>
                  {/* <AddToCalendarButton
                    trigger="click"
                    name={`${lead.submittedBy} - ${lead.municipality}`}
                    options={['Outlook.com', 'Apple', 'Google', 'Yahoo', 'iCal']}
                    startDate={toLocalTime(row.appointmentDate).format('YYYY-MM-DD')}
                    startTime={toLocalTime(row.appointmentDate).format('HH:mm')}
                    endTime={toLocalTime(row.appointmentDate).add(1, 'hour').format('HH:mm')}
                    timeZone="America/Puerto_Rico"
                    location={`${lead.addressLine1} ${lead.addressLine2}`}
                  /> */}
                </div>
              </div>
            ),
          },
          {
            title: t('appointment_date'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <DatePicker
                showTime={{ use12Hours: true }}
                format="YYYY-MM-DD hh:mm a"
                className="w-full"
                showHour
                inputReadOnly
                showMinute
                minuteStep={15}
                value={dayjs(row?.appointmentDate).subtract(4, 'hour')}
                onChange={(date) => {
                  const tempData = JSON.parse(JSON.stringify(lead));
                  for (let i = 0; i < tempData.appointments?.length; i++) {
                    if (tempData.appointments[i].id === row.id) {
                      tempData.appointments[i].appointmentDate = date.add(4, 'hour');
                    }
                  }
                  setLead(tempData);
                }}
                showSecond={false}
                disabled={loading}
                type="primary"
                size="small"
                renderExtraFooter={() => getMobileDatePickerFooter(t)}
                placement="bottomLeft"
              />
            ),
          },
          {
            title: t('status'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <Select
                className="w-full"
                value={row.appointmentStatus}
                disabled={loading}
                onChange={(obj) => {
                  const tempData = JSON.parse(JSON.stringify(lead));
                  for (let i = 0; i < tempData.appointments?.length; i++) {
                    if (tempData.appointments[i].id === row.id) {
                      tempData.appointments[i].appointmentStatus = obj;
                    }
                  }
                  setLead(tempData);
                }}
              >
                {_.map(appointmentStatuses, (g) => (
                  <Option value={g.id}>{t(g.name)}</Option>
                ))}
              </Select>
            ),
          },
          {
            title: t('type'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <Select
                className="w-full"
                value={row.type}
                disabled={loading}
                onChange={(obj) => {
                  const tempData = JSON.parse(JSON.stringify(lead));
                  for (let i = 0; i < tempData.appointments?.length; i++) {
                    if (tempData.appointments[i].id === row.id) {
                      tempData.appointments[i].type = obj;
                    }
                  }
                  setLead(tempData);
                }}
              >
                {_.map(appointmentTypes, (g) => (
                  <Option value={g.id}>{t(g.name)}</Option>
                ))}
              </Select>
            ),
          },
          {
            title: t('actions'),
            align: 'center',
            className: 'text-sm hidden md:table-cell',
            render: (row) => (
              <div className="flex">
                <div className="mx-auto space-x-2">
                  <Popconfirm
                    title={t('update_appointment_prompt')}
                    okText={t('yes')}
                    cancelText={t('no')}
                    onConfirm={() => update(row)}
                  >
                    <Button
                      type="primary"
                      size="small"
                      disabled={loading}
                    >
                      {t('update')}
                    </Button>
                  </Popconfirm>
                  <Popconfirm
                    title={t('delete_appointment_prompt')}
                    okText={t('yes')}
                    cancelText={t('no')}
                    onConfirm={() => remove(row.id)}
                  >
                    <Button
                      type="primary"
                      danger
                      size="small"
                      disabled={loading}
                    >
                      {t('delete')}
                    </Button>
                  </Popconfirm>
                </div>
              </div>
            ),
          },
        ]}
      />
    </Modal>
  );
}

export default LeadAppointmentsModal;
